(function (scope) {
  'use strict';
  if (!scope.customElements || scope.customElements.get('sonamaru-inventory')) return;

  class SonamaruInventory extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      if (!scope.SonamaruPreparedness) {
        for (const control of this.querySelectorAll('input, select, button')) control.disabled = true;
        const status = this.querySelector('[data-inventory-status]');
        try { status.textContent = JSON.parse(this.closest('.sm-place')?.querySelector('[data-sm-messages]')?.textContent || '{}').load_failed || ''; }
        catch (_error) { status.textContent = ''; }
        status.hidden = false;
        status.toggleAttribute('data-error', true);
        this.querySelector('[data-inventory-empty]').hidden = true;
        return;
      }
      this.ready = true;
      this.importVersion = (this.importVersion || 0) + 1;
      this.exportResources ??= new Map();
      this.filter ??= 'all';
      this.model = scope.SonamaruPreparedness;
      this.dictionary = this.model.messages(this);
      this.form = this.querySelector('[data-inventory-form]');
      this.status = this.querySelector('[data-inventory-status]');
      this.list = this.querySelector('[data-inventory-list]');
      this.importInput = this.querySelector('[data-inventory-import]');
      this.onImport = () => this.readBackup();
      this.importInput.addEventListener('change', this.onImport);
      this.onSubmit = event => { event.preventDefault(); this.saveItem(); };
      this.onCategory = () => this.updateUnit();
      this.onClick = event => this.handleClick(event);
      this.onCandidate = event => { if (event.detail?.inventoryId === this.id) this.prefill(event.detail); };
      this.onStorage = event => { if (event.key === this.model.STORAGE_KEY || event.key === null) this.refresh(); };
      this.onChange = () => this.refresh();
      this.onVisibility = () => { if (!document.hidden) this.refresh(); };
      this.form.addEventListener('submit', this.onSubmit);
      this.form.elements.category.addEventListener('change', this.onCategory);
      this.addEventListener('click', this.onClick);
      document.addEventListener('sonamaru:stock-candidate', this.onCandidate);
      document.addEventListener('sonamaru:stock-changed', this.onChange);
      window.addEventListener('storage', this.onStorage);
      window.addEventListener('pageshow', this.onChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.refresh();
      this.updateUnit();
    }

    disconnectedCallback() {
      this.importInput?.removeEventListener('change', this.onImport);
      this.resetBackup();
      this.form?.removeEventListener('submit', this.onSubmit);
      this.form?.elements.category.removeEventListener('change', this.onCategory);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('sonamaru:stock-candidate', this.onCandidate);
      document.removeEventListener('sonamaru:stock-changed', this.onChange);
      window.removeEventListener('storage', this.onStorage);
      window.removeEventListener('pageshow', this.onChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
      for (const [url, timer] of this.exportResources || []) { clearTimeout(timer); URL.revokeObjectURL(url); }
      this.exportResources?.clear();
      this.ready = false;
    }

    message(key, values) { return this.model.text(this.dictionary, key, values); }

    showStatus(key, error = false, values) {
      this.status.textContent = this.message(key, values);
      this.status.dataset.statusKey = key;
      this.status.hidden = false;
      this.status.toggleAttribute('data-error', error);
    }

    refresh() {
      try {
        this.state = this.model.read(window.localStorage);
        if (this.status.dataset.statusKey === 'load_failed') {
          this.status.hidden = true;
          this.status.removeAttribute('data-error');
        }
        this.querySelector('[data-inventory-save]').disabled = false;
        this.querySelector('[data-inventory-export]').disabled = false;
        this.querySelector('[data-inventory-clear]').disabled = false;
        this.importInput.disabled = false;
        if (this.backupCandidate) this.renderBackupPreview();
        for (const button of this.querySelectorAll('[data-inventory-filter]')) button.disabled = false;
        this.render();
      } catch (_error) {
        this.state = null;
        this.importInput.disabled = true;
        this.resetBackup();
        this.list.replaceChildren();
        this.querySelector('[data-inventory-summary]').textContent = '';
        this.querySelector('[data-inventory-empty]').hidden = true;
        this.querySelector('[data-inventory-save]').disabled = true;
        this.querySelector('[data-inventory-export]').disabled = true;
        for (const button of this.querySelectorAll('[data-inventory-filter]')) button.disabled = true;
        // Reset is an explicit user action; a read failure never overwrites data.
        this.querySelector('[data-inventory-clear]').disabled = false;
        this.showStatus('load_failed', true);
      }
    }

    updateUnit() {
      const category = this.form.elements.category.value;
      this.querySelector('[data-inventory-unit]').textContent = this.message(`unit_${category}`);
      this.querySelector('[data-inventory-amount-note]').textContent = this.message(`amount_note_${category}`);
      const amount = this.form.elements.amount;
      amount.step = category === 'water' ? '0.001' : '1';
      amount.min = category === 'water' ? '0.001' : '1';
      amount.inputMode = category === 'water' ? 'decimal' : 'numeric';
    }

    render() {
      const active = document.activeElement;
      const focusedAction = this.list.contains(active) && active.matches('[data-inventory-action]')
        ? { itemId: active.dataset.itemId, action: active.dataset.inventoryAction } : null;
      this.list.replaceChildren();
      const summary = this.model.summarize(this.state.items);
      this.querySelector('[data-inventory-summary]').textContent = this.message('summary', { water: summary.waterLitres.toLocaleString('ja-JP'), toilet: summary.toiletUses.toLocaleString('ja-JP') });
      const today = this.model.todayString();
      const ranked = this.state.items.map(item => ({ item, status: this.model.expiryStatus(item, today) }));
      const order = { expired: 0, soon: 1, unknown: 2, ready: 3 };
      ranked.sort((a, b) => order[a.status] - order[b.status] || (a.item.expiry || '9999').localeCompare(b.item.expiry || '9999') || a.item.name.localeCompare(b.item.name));
      const visible = ranked.filter(record => this.filter === 'all' || record.status === this.filter);
      for (const button of this.querySelectorAll('[data-inventory-filter]')) {
        const filter = button.dataset.inventoryFilter;
        const count = filter === 'all' ? ranked.length : ranked.filter(record => record.status === filter).length;
        button.textContent = `${this.message(`filter_${filter}`)} (${count})`;
        button.setAttribute('aria-pressed', String(filter === this.filter));
      }
      const filterStatus = this.querySelector('[data-inventory-filter-status]');
      filterStatus.hidden = this.filter === 'all';
      filterStatus.textContent = this.message('filter_result', { count: visible.length });
      const empty = this.querySelector('[data-inventory-empty]');
      empty.hidden = visible.length > 0;
      empty.textContent = this.message(this.filter === 'all' ? 'empty_inventory' : 'empty_filter');
      for (const { item, status } of visible) {
        const row = document.createElement('li');
        const name = document.createElement('strong');
        name.textContent = item.name;
        const amount = document.createElement('p');
        amount.textContent = `${this.message(item.category)} · ${item.amount.toLocaleString('ja-JP')}${this.message(`unit_${item.category}`)}`;
        const expiry = document.createElement('p');
        expiry.dataset.expiryState = status;
        expiry.textContent = this.message(status === 'unknown' ? 'unknown_expiry' : status, { date: item.expiry });
        if (status === 'soon' || status === 'expired') expiry.textContent += ` · ${item.expiry}`;
        const actions = document.createElement('div');
        actions.className = 'sm-inventory-row-actions';
        for (const [action, key] of [['edit', 'edit_item'], ['remove', 'remove_item']]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'sm-text-button';
          button.dataset.inventoryAction = action;
          button.dataset.itemId = item.id;
          button.textContent = this.message(key);
          button.setAttribute('aria-label', this.message(`${key}_label`, { name: item.name }));
          actions.append(button);
        }
        row.append(name, amount, expiry, actions);
        this.list.append(row);
      }
      if (focusedAction) {
        const replacement = [...this.list.querySelectorAll('[data-inventory-action]')]
          .find(button => button.dataset.itemId === focusedAction.itemId && button.dataset.inventoryAction === focusedAction.action);
        (replacement || this.querySelector('[data-inventory-title]')).focus({ preventScroll: true });
      }
    }

    commit(update, successKey) {
      this.lastCommitError = null;
      try {
        const latest = this.model.read(window.localStorage);
        this.state = this.model.write(window.localStorage, update(latest));
        document.dispatchEvent(new CustomEvent('sonamaru:stock-changed'));
        this.showStatus(successKey);
        return true;
      } catch (error) {
        this.lastCommitError = error.message;
        if (error.message === 'STALE_ITEM' || error.message === 'ITEM_LIMIT') this.refresh();
        this.showStatus(error.message === 'STALE_ITEM' ? 'item_changed' : error.message === 'ITEM_LIMIT' ? 'item_limit' : 'save_failed', true);
        return false;
      }
    }

    saveItem() {
      let item;
      try {
        const data = new FormData(this.form);
        const id = data.get('item_id') || (scope.crypto?.randomUUID ? scope.crypto.randomUUID() : `stock-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
        item = this.model.normalizeItem({ id, name: data.get('item_name'), category: data.get('category'), amount: data.get('amount'), expiry: data.get('expiry') });
      } catch (error) { this.showStatus(error.message === 'INVALID_DATE' ? 'invalid_date' : 'invalid_item', true); return; }
      if (this.state?.items.length >= 200 && !this.state.items.some(value => value.id === item.id)) { this.showStatus('item_limit', true); return; }
      const editing = this.form.elements.item_id.value !== '';
      const saved = this.commit(state => {
        const index = state.items.findIndex(value => value.id === item.id);
        // Another tab may have removed a record during editing. Do not resurrect it.
        if (editing && (index === -1 || JSON.stringify(state.items[index]) !== JSON.stringify(this.editingBaseline))) throw new Error('STALE_ITEM');
        if (index === -1 && state.items.length >= 200) throw new Error('ITEM_LIMIT');
        if (index === -1) state.items.push(item); else state.items[index] = item;
        return state;
      }, 'item_saved');
      if (saved) { this.filter = 'all'; this.render(); this.resetForm(); }
    }

    resetForm() {
      this.form.reset();
      this.form.elements.item_id.value = '';
      this.editingBaseline = null;
      this.querySelector('[data-inventory-save]').textContent = this.message('add_item');
      this.querySelector('[data-inventory-cancel]').hidden = true;
      this.updateUnit();
    }

    canSwitchRecord() {
      const fields = this.form.elements;
      if (fields.item_id.value || fields.item_name.value.trim() || fields.amount.value || fields.expiry.value || fields.category.value !== 'water') {
        this.showStatus('register_draft', true);
        this.querySelector('[data-inventory-cancel]').hidden = false;
        this.querySelector('[data-inventory-form-details]').open = true;
        fields.item_name.focus();
        return false;
      }
      return true;
    }

    prefill(candidate) {
      if (!candidate || typeof candidate.name !== 'string' || !Number.isFinite(candidate.litres) || candidate.litres <= 0 || !this.canSwitchRecord()) return;
      this.resetForm();
      this.form.elements.item_name.value = candidate.name.slice(0, 120);
      this.form.elements.category.value = 'water';
      this.form.elements.amount.value = String(candidate.litres);
      this.form.elements.expiry.value = '';
      this.updateUnit();
      this.showStatus('register_hint');
      this.querySelector('[data-inventory-form-details]').open = true;
      this.form.elements.amount.focus();
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-inventory-filter')) {
        if (!this.state || !['all', 'soon', 'expired', 'unknown'].includes(button.dataset.inventoryFilter)) return;
        this.filter = button.dataset.inventoryFilter;
        this.render();
        return;
      }
      if (button.hasAttribute('data-inventory-import-cancel')) { this.resetBackup(); return; }
      if (button.hasAttribute('data-inventory-restore')) { this.restoreBackup(); return; }
      if (button.hasAttribute('data-inventory-cancel')) { this.resetForm(); this.status.hidden = true; this.form.elements.item_name.focus(); return; }
      if (button.hasAttribute('data-inventory-export')) { this.exportRecords(); return; }
      if (button.hasAttribute('data-inventory-clear')) {
        try {
          window.localStorage.removeItem(this.model.STORAGE_KEY);
          this.deleted = null;
          this.querySelector('[data-inventory-undo]').hidden = true;
          this.resetForm();
          document.dispatchEvent(new CustomEvent('sonamaru:stock-changed'));
          this.showStatus('cleared');
        } catch (_error) { this.showStatus('save_failed', true); }
        return;
      }
      if (button.hasAttribute('data-inventory-undo')) {
        const item = this.deleted;
        if (!item) return;
        if (this.commit(state => {
          if (state.items.some(value => value.id === item.id)) throw new Error('STALE_ITEM');
          if (state.items.length >= 200) throw new Error('ITEM_LIMIT');
          state.items.push(item); return state;
        }, 'item_restored')) {
          this.deleted = null;
          button.hidden = true;
        } else if (this.lastCommitError === 'STALE_ITEM') { this.deleted = null; button.hidden = true; }
        return;
      }
      const item = this.state?.items.find(value => value.id === button.dataset.itemId);
      if (!item) return;
      if (button.dataset.inventoryAction === 'edit') {
        if (!this.canSwitchRecord()) return;
        this.editingBaseline = { ...item };
        this.form.elements.item_id.value = item.id;
        this.form.elements.item_name.value = item.name;
        this.form.elements.category.value = item.category;
        this.form.elements.amount.value = String(item.amount);
        this.form.elements.expiry.value = item.expiry;
        this.updateUnit();
        this.querySelector('[data-inventory-save]').textContent = this.message('update_item');
        this.querySelector('[data-inventory-cancel]').hidden = false;
        this.querySelector('[data-inventory-form-details]').open = true;
        this.form.elements.amount.focus();
      } else if (button.dataset.inventoryAction === 'remove') {
        if (this.commit(state => {
          const latest = state.items.find(value => value.id === item.id);
          if (!latest || JSON.stringify(latest) !== JSON.stringify(item)) throw new Error('STALE_ITEM');
          state.items = state.items.filter(value => value.id !== item.id); return state;
        }, 'item_deleted')) {
          this.deleted = item;
          this.querySelector('[data-inventory-undo]').hidden = false;
          this.querySelector('[data-inventory-undo]').focus({ preventScroll: true });
          if (this.form.elements.item_id.value === item.id) this.resetForm();
        }
      }
    }

    resetBackup() {
      this.importVersion = (this.importVersion || 0) + 1;
      this.backupCandidate = null;
      if (this.importInput) this.importInput.value = '';
      const apply = this.querySelector('[data-inventory-restore]');
      if (apply) { apply.disabled = true; apply.hidden = true; }
      const cancel = this.querySelector('[data-inventory-import-cancel]');
      if (cancel) cancel.hidden = true;
      const status = this.querySelector('[data-inventory-import-status]');
      if (status) { status.textContent = ''; status.hidden = true; status.removeAttribute('data-error'); }
    }

    backupMessage(key, error = false, values) {
      const status = this.querySelector('[data-inventory-import-status]');
      status.textContent = this.message(key, values);
      status.hidden = false;
      status.toggleAttribute('data-error', error);
    }

    renderBackupPreview() {
      const apply = this.querySelector('[data-inventory-restore]');
      try {
        const result = this.model.mergeBackup(this.model.read(window.localStorage), this.backupCandidate);
        apply.hidden = result.added === 0; apply.disabled = result.added === 0;
        this.backupMessage(result.added ? 'restore_preview' : 'restore_empty', false, { count: result.added, amount: result.skipped });
        return result;
      } catch (error) {
        apply.hidden = true; apply.disabled = true;
        this.backupMessage(error.message === 'ITEM_LIMIT' ? 'restore_limit' : 'restore_invalid', true);
        return null;
      }
    }

    async readBackup() {
      const file = this.importInput.files?.[0];
      this.resetBackup();
      if (!file || !this.state) return;
      const version = this.importVersion;
      this.querySelector('[data-inventory-import-cancel]').hidden = false;
      this.backupMessage('restore_reading');
      try {
        if (!Number.isFinite(file.size) || file.size <= 0 || file.size > 131072 || !/\.json$/i.test(file.name)) throw new Error('INVALID_BACKUP');
        const content = await file.text();
        if (!this.ready || version !== this.importVersion) return;
        this.backupCandidate = this.model.normalizeState(JSON.parse(content));
        this.renderBackupPreview();
      } catch (_error) {
        if (!this.ready || version !== this.importVersion) return;
        this.backupCandidate = null;
        this.backupMessage('restore_invalid', true);
      }
    }

    restoreBackup() {
      if (!this.backupCandidate) return;
      try {
        const result = this.renderBackupPreview();
        if (!result?.added) return;
        this.model.write(window.localStorage, result.state);
        this.resetBackup();
        this.filter = 'all';
        document.dispatchEvent(new CustomEvent('sonamaru:stock-changed'));
        this.showStatus('restore_done', false, { count: result.added });
      } catch (_error) { this.showStatus('save_failed', true); }
    }

    exportRecords() {
      let url, link;
      try {
        const state = this.model.read(window.localStorage);
        url = URL.createObjectURL(new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' }));
        link = document.createElement('a');
        link.href = url;
        link.download = `sonamaru-stock-${this.model.todayString()}.json`;
        link.hidden = true;
        this.append(link);
        link.click();
      } catch (_error) { this.showStatus('export_failed', true); }
      finally {
        link?.remove();
        if (url) {
          const timer = setTimeout(() => { URL.revokeObjectURL(url); this.exportResources.delete(url); }, 1000);
          this.exportResources.set(url, timer);
        }
      }
    }
  }
  scope.customElements.define('sonamaru-inventory', SonamaruInventory);
})(typeof globalThis !== 'undefined' ? globalThis : window);
